import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { CURRENCY } from '../entities/project/project'
import { requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de proyectos. Quinto catálogo del backoffice
 * en v2 y el más rico: un proyecto pertenece a una sociedad (referencia `soc_`,
 * el mismo molde de `$lookup` que estrenaron agencias y sociedades) y puede exigir
 * autorización de reservas por una lista de usuarios autorizadores (`usr_`).
 *
 * `name` y `code` son únicos GLOBALES (a diferencia del `code` por empresa de las
 * agencias). `code` es el prefijo de los códigos de subtipos e inmuebles.
 */

/**
 * Referencia a la sociedad dueña, wire-safe: el `_id` no cruza el adapter, así que
 * se publica su `soc_`. Nullable por robustez del listado —igual que la autoría—:
 * una fila con la sociedad sin `publicId` presenta `null` en vez de tumbar la
 * página entera; `society` es required en el dominio, así que en la práctica
 * siempre se resuelve.
 */
export const V2AdminProjectSocietySchema = z
  .object({
    id: PublicIdSchemas.society,
    name: z.string()
  })
  .nullable()

/** Autoría/autorizador wire-safe: `usr_`, nunca el ObjectId. */
export const V2AdminProjectUserRefSchema = z.object({
  id: PublicIdSchemas.user,
  fullname: z.string()
})

/** La autoría puede faltar (usuario sin `publicId` o campo nulo). */
export const V2AdminProjectAuthorSchema = V2AdminProjectUserRefSchema.nullable()

/** Fila del listado y del detalle. */
export const V2AdminProjectSchema = z.object({
  id: PublicIdSchemas.project,
  name: z.string().min(1),
  /** Único (case-insensitive) entre todos los proyectos; prefijo de subtipos/inmuebles. */
  code: z.string().min(1),
  society: V2AdminProjectSocietySchema,
  currency: z.nativeEnum(CURRENCY),
  /** URL de la imagen/logo del proyecto (la consumen los PDFs generados). */
  image: z.string().nullable(),
  /**
   * El LOGO CUADRADO del proyecto — la marca que la app del cliente pinta sobre la portada.
   *
   * ── POR QUÉ NO ALCANZA CON `image` ─────────────────────────────────────────
   *
   * `image` es el logotipo COMPLETO: escudo, nombre y tagline, pensado para la papelería y los PDF
   * generados. Metido en una caja de 32 puntos el texto se vuelve pelusa. Son dos piezas de marca
   * distintas con dos usos distintos, no dos tamaños de la misma.
   *
   * ── POR QUÉ 1024×1024 EXACTOS ──────────────────────────────────────────────
   *
   * Porque es el tamaño con el que diseño entrega un ícono adaptado, y porque una regla exacta es
   * la única que se puede verificar en las dos capas sin discutir: «cuadrado y grande» obliga a
   * elegir un mínimo, y el primer logo que quede a 900 va a abrir la conversación de si 900 alcanza.
   * La validación corre en el navegador —antes de subir— y otra vez en el servidor.
   *
   * `null` mientras no se cargue. La app dibuja la inicial del proyecto, que es honesto y legible.
   */
  mark: z.string().nullable(),
  /**
   * Dónde QUEDA el proyecto, en una línea legible: «Zona 4 de Mixco, Guatemala».
   *
   * Va en el PROYECTO y no en el inmueble ni en el subtipo porque es lo único de los tres
   * que comparte una ubicación: los 103 inmuebles de un proyecto están en el mismo lugar.
   * Cargarla por unidad sería escribir la misma dirección 103 veces y garantizar que se
   * escriban distinto.
   *
   * TEXTO LIBRE de una línea, no un domicilio estructurado. Lo que la app dibuja es un
   * renglón bajo el nombre del inmueble; partirlo en calle/zona/municipio obligaría a
   * decidir un formato para un país cuyas direcciones no lo tienen, y a recomponerlo en
   * cada consumidor. El día que haga falta geocodificar, eso es otro campo.
   *
   * `null` mientras no se cargue — hoy no la tiene ninguno. La app esconde el renglón.
   */
  address: z.string().nullable(),
  requiresAuthorization: z.boolean(),
  /** Autorizadores de reservas, resueltos a `usr_`. Vacío si no requiere autorización. */
  authorizers: z.array(V2AdminProjectUserRefSchema),
  active: z.boolean(),
  createdBy: V2AdminProjectAuthorSchema,
  updatedBy: V2AdminProjectAuthorSchema,
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminProjectsPageSchema = z.object({
  data: z.array(V2AdminProjectSchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y faceta `active` con los mismos filtros allowlist del listado. La
 * faceta ignora el filtro de su propia dimensión; `total` respeta la consulta
 * completa. La sociedad no es una faceta: se filtra por nombre (texto).
 */
export const V2AdminProjectsSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/**
 * Entrada mínima del selector de proyectos. `society` (`soc_`) y `currency`
 * permiten a los formularios de inventario y negociación cascadear y preformatear.
 */
export const V2AdminProjectLookupItemSchema = z.object({
  id: PublicIdSchemas.project,
  name: z.string().min(1),
  code: z.string().min(1),
  currency: z.nativeEnum(CURRENCY),
  society: PublicIdSchemas.society
})

export const V2AdminProjectLookupSchema = z.object({
  data: z.array(V2AdminProjectLookupItemSchema)
})

/**
 * Alta de proyecto. La sociedad entra por `soc_` (no por ObjectId como el wire
 * legacy) y los autorizadores por `usr_`; el service los resuelve y valida. `name`
 * y `code` se recortan y deben ser únicos (case-insensitive) globalmente. El
 * refine exige ≥1 autorizador cuando `requiresAuthorization` está activo.
 */
export const V2CreateAdminProjectSchema = z
  .object({
    name: requiredString()
      .transform((value) => value.trim())
      .pipe(requiredString()),
    code: requiredString()
      .transform((value) => value.trim())
      .pipe(requiredString()),
    society: PublicIdSchemas.society,
    currency: z.nativeEnum(CURRENCY, {
      errorMap: () => ({ message: 'Debe seleccionar una moneda' })
    }),
    image: z
      .string()
      .nullish()
      .transform((value) => value ?? undefined),
    /*
      El logo cuadrado de la app. Mismo molde que `address`, y por el mismo motivo:

      ☠️ EL `.optional()` VA AL FINAL, DESPUÉS DEL TRANSFORM. Envolver al revés deja el
      `ZodEffects` por fuera y el generador de OpenAPI publica la propiedad como `required`
      aunque el tipo diga que es opcional — con eso, crear un proyecto sin logo dejaba de ser
      posible y el gate de cambios rompientes lo rechazaba.

      Los tres estados llegan distintos al servicio:
      ausente = no lo toques · una URL = guardala · `null` o vacío = borralo.
    */
    mark: z
      .string()
      .nullable()
      .transform((value) => value?.trim() || null)
      .optional(),
    /*
      Se recorta y una cadena vacía cuenta como AUSENTE: quien borra el campo en el
      formulario deja `''`, y guardar eso haría que la app dibuje un renglón en blanco bajo
      el nombre del inmueble en vez de esconderlo.
    */
    /*
      ☠️ EL `.optional()` VA AL FINAL, DESPUÉS DEL TRANSFORM, y no es estilo.

      Envolver al revés deja el `ZodEffects` por fuera, y el generador de OpenAPI mira ESE
      envoltorio: publica la propiedad como `required` aunque el tipo de TypeScript diga que
      es opcional. Efecto: no se podía crear un proyecto sin dirección, y el gate de cambios
      rompientes lo rechazaba con «added the new required request property». Con el
      `.optional()` afuera, el transform sólo corre cuando hay valor.

      Los tres estados llegan distintos al servicio:
      ausente = no lo toques · texto = guardalo · `null` o sólo espacios = borralo.
    */
    address: z
      .string()
      .max(200)
      .nullable()
      .transform((value) => value?.trim() || null)
      .optional(),
    requiresAuthorization: z.boolean().optional(),
    authorizerIds: z.array(PublicIdSchemas.user).optional(),
    active: z.boolean().optional()
  })
  .superRefine((value, ctx) => {
    if (value.requiresAuthorization && (value.authorizerIds?.length ?? 0) === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['authorizerIds'],
        message: 'Debe seleccionar al menos un autorizador'
      })
    }
  })

/**
 * Edición parcial SIN el refine: un PATCH con `requiresAuthorization: true` y sin
 * `authorizerIds` deja la lista existente intacta (semántica de patch). Un id
 * desconocido responde 404: nunca hace upsert.
 */
export const V2UpdateAdminProjectSchema = z
  .object({
    name: requiredString()
      .transform((value) => value.trim())
      .pipe(requiredString()),
    code: requiredString()
      .transform((value) => value.trim())
      .pipe(requiredString()),
    society: PublicIdSchemas.society,
    currency: z.nativeEnum(CURRENCY),
    image: z
      .string()
      .nullish()
      .transform((value) => value ?? undefined),
    /*
      El logo cuadrado de la app. Mismo molde que `address`, y por el mismo motivo:

      ☠️ EL `.optional()` VA AL FINAL, DESPUÉS DEL TRANSFORM. Envolver al revés deja el
      `ZodEffects` por fuera y el generador de OpenAPI publica la propiedad como `required`
      aunque el tipo diga que es opcional — con eso, crear un proyecto sin logo dejaba de ser
      posible y el gate de cambios rompientes lo rechazaba.

      Los tres estados llegan distintos al servicio:
      ausente = no lo toques · una URL = guardala · `null` o vacío = borralo.
    */
    mark: z
      .string()
      .nullable()
      .transform((value) => value?.trim() || null)
      .optional(),
    /*
      Igual que en el alta: se recorta y una cadena vacía se reduce a `null`, que en el
      PATCH significa BORRALA. Guardar `''` haría que la app dibuje un renglón en blanco
      bajo el nombre del inmueble en vez de esconderlo.
    */
    /*
      ☠️ EL `.optional()` VA AL FINAL, DESPUÉS DEL TRANSFORM, y no es estilo.

      Envolver al revés deja el `ZodEffects` por fuera, y el generador de OpenAPI mira ESE
      envoltorio: publica la propiedad como `required` aunque el tipo de TypeScript diga que
      es opcional. Efecto: no se podía crear un proyecto sin dirección, y el gate de cambios
      rompientes lo rechazaba con «added the new required request property». Con el
      `.optional()` afuera, el transform sólo corre cuando hay valor.

      Los tres estados llegan distintos al servicio:
      ausente = no lo toques · texto = guardalo · `null` o sólo espacios = borralo.
    */
    address: z
      .string()
      .max(200)
      .nullable()
      .transform((value) => value?.trim() || null)
      .optional(),
    requiresAuthorization: z.boolean(),
    authorizerIds: z.array(PublicIdSchemas.user),
    active: z.boolean()
  })
  .partial()

export type V2AdminProjectSociety = z.infer<typeof V2AdminProjectSocietySchema>
export type V2AdminProjectUserRef = z.infer<typeof V2AdminProjectUserRefSchema>
export type V2AdminProjectAuthor = z.infer<typeof V2AdminProjectAuthorSchema>
export type V2AdminProject = z.infer<typeof V2AdminProjectSchema>
export type V2AdminProjectsPage = z.infer<typeof V2AdminProjectsPageSchema>
export type V2AdminProjectsSummary = z.infer<typeof V2AdminProjectsSummarySchema>
export type V2AdminProjectLookupItem = z.infer<typeof V2AdminProjectLookupItemSchema>
export type V2AdminProjectLookup = z.infer<typeof V2AdminProjectLookupSchema>
export type V2CreateAdminProjectInput = z.infer<typeof V2CreateAdminProjectSchema>
export type V2UpdateAdminProjectInput = z.infer<typeof V2UpdateAdminProjectSchema>
